import {firstNonEmptyOr} from '../../../core/helpers/nonEmpty';
/* eslint-disable @typescript-eslint/no-explicit-any */
import {Api} from '@common/constants';
import {get} from '@core/services/httpMethods';
import BASE_URL from '@core/helpers/constants';
import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import store, {RootState} from '@store';

const getTenantId = () => store.getState().auth?.user?.tenantId;

// Component DTOs (adjust these according to your API)
export interface InputComponentDTO {
  id: string;
  type: 'input';
  [key: string]: any;
}

export interface RulesetComponentDTO {
  id: string;
  type: 'ruleset';
  [key: string]: any;
}

// API response type
export interface Workflow {
  data(
    data: any,
    arg1: null,
    arg2: number
  ): import('react').SetStateAction<string>;
  id: string;
  name: string;
  status: string;
  components?: Array<InputComponentDTO | RulesetComponentDTO>;
}

interface WorkflowState {
  data: Workflow | null;
  loading: boolean;
  error: string | null;
  inputComponents: Record<string, InputComponentDTO>;
  rulesetComponents: Record<string, RulesetComponentDTO>;
}

const initialState: WorkflowState = {
  data: null,
  loading: false,
  error: null,
  inputComponents: {},
  rulesetComponents: {},
};

// Async thunk with inline typing for get()
export const fetchWorkflow = createAsyncThunk<
  Workflow, // return type
  string, // argument type
  {rejectValue: string}
>('workflow/fetchWorkflow', async (id, {rejectWithValue}) => {
  try {
    const tenantId = getTenantId();
    const url = `${BASE_URL}${Api.RULE_ENGINE_BASE_URL}${Api.WORKFLOWS}/${id}`;
    const data = (await get(url, {
      headers: {'X-Tenant-ID': tenantId},
      params: {status: 'DRAFT'},
    })) as Workflow;
    return {
      ...data,
      components: data.components ?? [], // fallback to empty array
    };
  } catch {
    return rejectWithValue('Failed to fetch workflow');
  }
});

const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    clearWorkflow: (state) => {
      state.data = null;
      state.error = null;
      state.inputComponents = {};
      state.rulesetComponents = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchWorkflow.fulfilled,
        (state, action: PayloadAction<Workflow>) => {
          state.loading = false;
          state.data = action.payload;

          // Reset component maps
          state.inputComponents = {};
          state.rulesetComponents = {};

          // Safely loop components if available
          action.payload.components?.forEach((component) => {
            if (component.type === 'input') {
              state.inputComponents[component.id] = component;
            } else if (component.type === 'ruleset') {
              state.rulesetComponents[component.id] = component;
            }
          });
        }
      )
      .addCase(fetchWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Error fetching workflow',
          action.payload
        );
      });
  },
});

export const {clearWorkflow} = workflowSlice.actions;
export const selectWorkflow = (state: RootState) => state.workflow;
export default workflowSlice.reducer;
